import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import baseuri from '../../baseuri';
import { getUserWalletBalance } from './user-profile';

export const retrieveDoubleFundings = createAsyncThunk(
	'compliance/retrieveDoubleFundings',
	async (params: any) => {
		const res = await baseuri.get('/admin/compliance/double-fundings', {
			params,
		});
		return res.data.data;
	}
);

export const retrieveFundingRequests = createAsyncThunk(
	'compliance/retrieveFundingRequests',
	async (params: any) => {
		const res = await baseuri.get('/admin/compliance/funding-requests', {
			params,
		});
		return res.data.data;
	}
);

export const retrieveNegativeAccountBalances = createAsyncThunk(
	'compliance/retrieveNegativeAccountBalances',
	async (params: any) => {
		const res = await baseuri.get('/admin/compliance/negative-balances', {
			params,
		});
		return res.data.data;
	}
);

export const retrieveNegativeFlexibleSavingsBalances = createAsyncThunk(
	'compliance/retrieveNegativeFlexibleSavingsBalances',
	async (params: any) => {
		const res = await baseuri.get(
			'/admin/compliance/negative-flexible-savings-balances',
			{
				params,
			}
		);
		return res.data.data;
	}
);

export const retrieveBlacklistedUsers = createAsyncThunk(
	'compliance/retrieveBlacklistedUsers',
	async (params: any) => {
		const res = await baseuri.get('/admin/blacklisted-device', {
			params,
		});
		return res.data.data;
	}
);

export const addBlacklistedUser = async (data: any) => {
	try {
		await baseuri.post('/admin/blacklisted-device', data);

		message.success('Added Successfully!');

		return null;
	} catch (e: any) {
		message.error(`Add failed! ${e.toString()}`);
		return null;
	}
};

export const updateBlacklistedUser = async (data: any) => {
	try {
		await baseuri.put('/admin/blacklisted-device', data);

		message.success('Updated Successfully!');

		return null;
	} catch (e: any) {
		message.error(`Update failed! ${e.toString()}`);
		return null;
	}
};

export const deleteBlacklistedUser = async (params: any) => {
	try {
		await baseuri.delete('/admin/blacklisted-device', { params });

		message.success('Deleted Successfully!');

		return null;
	} catch (e: any) {
		message.error(`Deleting failed! ${e.toString()}`);
		return null;
	}
};

export const resetUserBalance = createAsyncThunk(
	'compliance/resetUserBalance',
	async (data: any, { dispatch }) => {
		await baseuri.put('/admin/users/reset-user-balance', data);
		message.success('Reset Complete!');
		dispatch(getUserWalletBalance(data.userId));
	}
);

export const getRequestLogs = createAsyncThunk(
	'compliance/getRequestLogs',
	async (params: any) => {
		const res = await baseuri.get('/admin/compliance/requestlogs', {
			params,
		});
		return res.data.data;
	}
);

export const retrieveAegisThreats = createAsyncThunk(
	'compliance/retrieveAegisThreats',
	async (params: any) => {
		const res = await baseuri.get('/admin/security/aegis/threats', {
			params,
		});
		return res.data.data;
	}
);

export const retrieveAegisDependencies = createAsyncThunk(
	'compliance/retrieveAegisDependencies',
	async () => {
		const res = await baseuri.get('/admin/security/aegis/dependencies');
		return res.data.data;
	}
);

export const retrieveAegisPosture = createAsyncThunk(
	'compliance/retrieveAegisPosture',
	async () => {
		const res = await baseuri.get('/admin/security/aegis/posture');
		return res.data.data;
	}
);

export const retrieveAegisRecipients = createAsyncThunk(
	'compliance/retrieveAegisRecipients',
	async () => {
		const res = await baseuri.get('/admin/security/aegis/admin-recipients');
		return res.data.data;
	}
);

export const addAegisRecipient = async (email: string) => {
	try {
		await baseuri.get('/admin/security/aegis/admin-recipients', {
			params: { email },
		});
		message.success('Recipient added successfully!');
		return true;
	} catch (e: any) {
		message.error(`Failed to add recipient: ${e.toString()}`);
		return false;
	}
};

export const deleteAegisRecipient = async (email: string) => {
	try {
		await baseuri.delete('/admin/security/aegis/admin-recipients', { data: { email } });
		message.success('Recipient removed successfully!');
		return true;
	} catch (e: any) {
		message.error(`Failed to remove recipient: ${e.toString()}`);
		return false;
	}
};

export const retrieveAegisReports = createAsyncThunk(
	'compliance/retrieveAegisReports',
	async (params: any) => {
		const res = await baseuri.get('/admin/security/aegis/reports', {
			params,
		});
		return res.data.data;
	}
);

export const exportAegisReport = async (data: { adminEmails: string[] }) => {
	try {
		await baseuri.post('/admin/security/aegis/reports/export', data);
		message.success('Report export initiated successfully!');
		return true;
	} catch (e: any) {
		message.error(`Failed to export report: ${e.toString()}`);
		return false;
	}
};

export const triggerAegisManualScan = async () => {
	try {
		await baseuri.post('/admin/security/aegis/manual-scan');
		message.success('Manual scan triggered successfully!');
		return true;
	} catch (e: any) {
		message.error(`Failed to trigger manual scan: ${e.toString()}`);
		return false;
	}
};

export const retrieveAegisStatus = createAsyncThunk(
	'compliance/retrieveAegisStatus',
	async () => {
		const res = await baseuri.get('/admin/security/aegis/status');
		return res.data.data;
	}
);

export const resolveAegisThreat = async (threatId: string) => {
	try {
		await baseuri.post(`/admin/security/aegis/threats/${threatId}/resolve`);
		message.success('Threat resolved successfully!');
		return true;
	} catch (e: any) {
		message.error(`Failed to resolve threat: ${e.toString()}`);
		return false;
	}
};

export const resolveAllAegisThreats = async () => {
	try {
		await baseuri.post('/admin/security/aegis/threats/resolve-all');
		message.success('All threats resolved successfully!');
		return true;
	} catch (e: any) {
		message.error(`Failed to resolve all threats: ${e.toString()}`);
		return false;
	}
};

export const getSingleRequestLogs = createAsyncThunk(
	'compliance/getSingleRequestLogs',
	async (data: any) => data
);

const complianceRx = createSlice({
	name: 'compliance',
	initialState: {
		fundingRequests: {
			itemsList: [],
			paginator: {
				itemCount: 0,
				perPage: 20,
				currentPage: 1,
			},
		},
		negativeAccounts: {
			itemsList: [],
			paginator: {
				itemCount: 0,
				perPage: 20,
				currentPage: 1,
			},
		},
		negativeFlexibleAccounts: {
			itemsList: [],
			paginator: {
				itemCount: 0,
				perPage: 20,
				currentPage: 1,
			},
		},
		blacklistedUsers: {
			itemsList: [],
			paginator: {
				itemCount: 0,
				perPage: 20,
				currentPage: 1,
			},
		},
		doubleFunding: {
			itemsList: [],
			paginator: {
				itemCount: 0,
				perPage: 20,
				currentPage: 1,
			},
		},
		requestLogs: {
			itemsList: [],
			paginator: {
				itemCount: 0,
				perPage: 20,
				currentPage: 1,
			},
		},
		singleRequestLogs: {
			all: [],
			bodies: [
				{
					Currency: '',
					Remittances: [],
					ReportGeneratedAt: '',
					ReportId: '',
					ReportPeriodEnd: '',
					ReportPeriodStart: '',
					TotalAmount: 0,
					TotalSuccessfulRemittances: 0,
				},
			],
			client_details: null,
			createdAt: '',
			deviceType: '',
			headers: [],
			method: null,
			queries: [],
			status: '',
			updatedAt: '',
			url: [],
			user_session: [],
			__v: 0,
			_id: '',
		},
		aegisThreats: {
			records: [],
			currentPage: 0,
			totalPages: 0,
			totalRecords: 0,
			pageSize: 0,
		},
		aegisStatus: {
			status: '',
			counters: {
				activeThreats: 0,
				criticalThreats: 0,
				threatsToday: 0,
				totalVulnerabilities: 0,
			}
		},
		aegisReports: {
			records: [],
			currentPage: 0,
			totalPages: 0,
			totalRecords: 0,
			pageSize: 0,
		},
		aegisDependencies: {
			dependencies: {},
			devDependencies: {},
		},
		aegisPosture: {
			overall: '',
			counts: { pass: 0, warn: 0, fail: 0 },
			checks: [],
			checkedAt: '',
		},
		aegisRecipients: {
			records: [],
		},
	},
	reducers: {},
	extraReducers: (builder) =>
		builder
			.addCase(retrieveDoubleFundings.fulfilled, (store, action) => {
				const state = store;
				state.doubleFunding = action.payload;
			})
			.addCase(retrieveFundingRequests.fulfilled, (store, action) => {
				const state = store;
				state.fundingRequests = action.payload;
			})
			.addCase(retrieveNegativeAccountBalances.fulfilled, (store, action) => {
				const state = store;
				state.negativeAccounts = action.payload;
			})
			.addCase(
				retrieveNegativeFlexibleSavingsBalances.fulfilled,
				(store, action) => {
					const state = store;
					state.negativeFlexibleAccounts = action.payload;
				}
			)
			.addCase(retrieveBlacklistedUsers.fulfilled, (store, action) => {
				const state = store;
				state.blacklistedUsers = action.payload;
			})
			.addCase(getRequestLogs.fulfilled, (store, action) => {
				const state = store;
				state.requestLogs = action.payload;
			})
			.addCase(getSingleRequestLogs.fulfilled, (store, action) => {
				const state = store;
				state.singleRequestLogs = action.payload;
			})
			.addCase(retrieveAegisThreats.fulfilled, (store, action) => {
				const state = store;
				state.aegisThreats = action.payload;
			})
			.addCase(retrieveAegisStatus.fulfilled, (store, action) => {
				const state = store;
				state.aegisStatus = action.payload;
			})
			.addCase(retrieveAegisReports.fulfilled, (store, action) => {
				const state = store;
				state.aegisReports = action.payload;
			})
			.addCase(retrieveAegisDependencies.fulfilled, (store, action) => {
				const state = store;
				state.aegisDependencies = action.payload;
			})
			.addCase(retrieveAegisPosture.fulfilled, (store, action) => {
				const state = store;
				state.aegisPosture = action.payload;
			})
			.addCase(retrieveAegisRecipients.fulfilled, (store, action) => {
				const state = store;
				state.aegisRecipients = action.payload;
			}),
});

export default complianceRx.reducer;
